import React, { useState } from "react";
import { useParams, Redirect, Link } from "react-router-dom";
import AnimatedSection from "@/components/animation/AnimatedSection/AnimatedSection";
import { useTranslation } from "react-i18next";
import OptImg from "@/components/OptImg";

// images stay in code (not translated)
const IMAGES = {
  buy: {
    hero: "https://res.cloudinary.com/dt1jmjt25/image/upload/v1761237548/Bagian_buy_xmzlge.jpg",
    first:
      "https://res.cloudinary.com/dt1jmjt25/image/upload/v1761237548/Bagian_buy_xmzlge.jpg",
    second:
      "https://res.cloudinary.com/dt1jmjt25/image/upload/v1761237548/Bagian_buy_xmzlge.jpg",
  },
  rent: {
    hero: "https://res.cloudinary.com/dt1jmjt25/image/upload/v1761237549/Bagian_rent_qvhskt.jpg",
    first:
      "https://res.cloudinary.com/dt1jmjt25/image/upload/v1761237549/Bagian_rent_qvhskt.jpg",
    second:
      "https://res.cloudinary.com/dt1jmjt25/image/upload/v1761237549/Bagian_rent_qvhskt.jpg",
  },
};

// simple icon set reused by index
const featureIcons = {
  buy: [
    /* 0 */ <svg
      width="32"
      height="32"
      fill="none"
      stroke="#111"
      strokeWidth="2"
      viewBox="0 0 32 32"
    >
      <circle cx="16" cy="16" r="13" />
      <path d="M12 20l4-8 4 8" />
    </svg>,
    /* 1 */ <svg
      width="32"
      height="32"
      fill="none"
      stroke="#111"
      strokeWidth="2"
      viewBox="0 0 32 32"
    >
      <rect x="8" y="10" width="16" height="12" rx="2" />
      <path d="M12 14h8v4h-8z" />
    </svg>,
    /* 2 */ <svg
      width="32"
      height="32"
      fill="none"
      stroke="#111"
      strokeWidth="2"
      viewBox="0 0 32 32"
    >
      <path d="M6 26v-6h6v6" />
      <path d="M20 26v-14h6v14" />
      <rect x="14" y="18" width="4" height="8" />
    </svg>,
    /* 3 */ <svg
      width="32"
      height="32"
      fill="none"
      stroke="#111"
      strokeWidth="2"
      viewBox="0 0 32 32"
    >
      <circle cx="16" cy="16" r="13" />
      <path d="M12 20l8-8" />
    </svg>,
  ],
  rent: [
    /* 0 */ <svg
      width="32"
      height="32"
      fill="none"
      stroke="#007"
      strokeWidth="2"
      viewBox="0 0 32 32"
    >
      <circle cx="16" cy="16" r="13" />
      <path d="M11 18h10M16 13v10" />
    </svg>,
    /* 1 */ <svg
      width="32"
      height="32"
      fill="none"
      stroke="#007"
      strokeWidth="2"
      viewBox="0 0 32 32"
    >
      <rect x="8" y="10" width="16" height="12" rx="2" />
      <path d="M12 18h8" />
    </svg>,
    /* 2 */ <svg
      width="32"
      height="32"
      fill="none"
      stroke="#007"
      strokeWidth="2"
      viewBox="0 0 32 32"
    >
      <path d="M6 26v-8h20v8" />
      <rect x="10" y="10" width="12" height="6" />
    </svg>,
    /* 3 */ <svg
      width="32"
      height="32"
      fill="none"
      stroke="#007"
      strokeWidth="2"
      viewBox="0 0 32 32"
    >
      <circle cx="16" cy="16" r="13" />
      <path d="M16 10v8h8" />
    </svg>,
  ],
};

const FeatureSection = ({ features, headline, subtitle, serviceKey }) => (
  <section className="w-full max-w-7xl mx-auto flex flex-col lg:flex-row gap-12 px-4 sm:px-8 py-16">
    <div className="flex-1 flex flex-col justify-center mb-8 lg:mb-0">
      <h1 className="text-3xl md:text-5xl font-extrabold text-black mb-2 leading-tight">
        {headline}
      </h1>
      <div className="mt-3 text-lg md:text-2xl font-semibold text-gray-500">
        {subtitle}
      </div>
    </div>
    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-10">
      {features.map((f, idx) => (
        <div
          key={f.title}
          className="flex flex-col items-start p-6 rounded-2xl group hover:shadow-xl transition bg-white"
        >
          <div className="w-14 h-14 mb-4 flex items-center justify-center rounded-2xl shadow-inner group-hover:scale-105 group-hover:bg-[#1746A2]/10 transition-all text-3xl">
            {featureIcons[serviceKey][idx]}
          </div>
          <div className="font-bold text-lg text-black mb-1">{f.title}</div>
          <div className="text-base text-left text-gray-700 leading-relaxed text-left">
            {f.desc}
          </div>
        </div>
      ))}
    </div>
  </section>
);

// First/SecondExplanationSection, BenefitsSection, FaqSection stay the same as your version,
// just consume text passed from translations.

const FirstExplanationSection = ({ data, image }) => (
  <section className="w-full bg-gradient-to-br from-[#111919] via-[#151f24] to-[#22323e] py-14 px-4 flex justify-center">
    <div className="w-full max-w-7xl flex flex-col-reverse lg:flex-row items-center rounded-3xl overflow-hidden shadow-lg">
      <div className="w-full lg:w-1/2 flex flex-col justify-center p-8 md:p-12">
        <div className="w-16 h-2 rounded-full bg-[#c9785a] mb-6" />
        <h2 className="text-[2.2rem] sm:text-[2.6rem] lg:text-[3rem] font-extrabold leading-tight mb-4 text-white drop-shadow">
          {data.title}
        </h2>
        <div className="text-base sm:text-lg text-[#dbeafe] mb-6 leading-relaxed text-left space-y-4">
          {String(data.body)
            .split(/\n{2,}/)
            .map((p, i) => (
              <p key={i}>{p}</p>
            ))}
        </div>

        <ul className="flex flex-wrap gap-3 mt-4 mb-2">
          {data.highlights.map((text, idx) => (
            <li
              key={idx}
              className="bg-[#c9785a]/90 text-white text-[0.98rem] font-semibold rounded-full px-4 py-1 shadow text-left"
            >
              {text}
            </li>
          ))}
        </ul>
      </div>
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6">
        <div className="rounded-3xl overflow-hidden shadow-2xl bg-[#1b2830] flex items-center justify-center">
          <OptImg
            src={image}
            alt=""
            className="w-full h-64 md:h-80 object-cover object-center rounded-3xl transition duration-300 hover:scale-105"
          />
        </div>
      </div>
    </div>
  </section>
);

const SecondExplanationSection = ({ data, image }) => (
  <section className="w-full bg-gradient-to-br from-white to-[#e8f0fa] py-14 px-4 flex justify-center">
    <div className="w-full max-w-7xl flex flex-col lg:flex-row items-center rounded-3xl overflow-hidden shadow-lg">
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6">
        <div className="rounded-3xl overflow-hidden shadow-2xl bg-[#e8f0fa] flex items-center justify-center">
          <OptImg
            src={image}
            alt=""
            className="w-full h-64 md:h-80 object-cover object-center rounded-3xl transition duration-300 hover:scale-105"
          />
        </div>
      </div>
      <div className="w-full lg:w-1/2 flex flex-col justify-center p-8 md:p-12">
        <div className="w-12 h-2 rounded-full bg-[#1746A2] mb-5" />
        <h2 className="text-[2.1rem] sm:text-[2.5rem] lg:text-[2.8rem] font-extrabold leading-tight mb-6 text-[#1a1a1a] drop-shadow">
          {data.title}
        </h2>
        <div className="text-base sm:text-lg text-[#233] leading-relaxed space-y-6 text-left">
          {Array.isArray(data.body) ? (
            data.body.map((t, idx) => <p key={idx}>{t}</p>)
          ) : (
            <p>{data.body}</p>
          )}
        </div>
      </div>
    </div>
  </section>
);

const BenefitsSection = ({ benefits, title }) => (
  <section className="w-full bg-white flex justify-center py-16 px-2">
    <div className="w-full max-w-7xl flex flex-col lg:flex-row">
      <div className="flex-1 flex flex-col justify-center mb-10 lg:mb-0">
        <h2 className="text-[2.5rem] lg:text-[3.4rem] font-extrabold leading-tight">
          {title}
        </h2>
      </div>
      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
        {benefits.map((item) => (
          <div
            key={item.title}
            className="flex flex-col sm:flex-row items-start"
          >
            <div className="w-14 h-14 flex items-center justify-center border-2 border-dashed border-gray-400 rounded-[14px] mr-4 flex-shrink-0 mb-2 sm:mb-0">
              {/* keep your icons as-is or pick from a set */}
              {/* You can also add a benefitIcons[] similar to featureIcons if needed */}
              <span>★</span>
            </div>
            <div>
              <div className="text-lg font-bold mb-1">{item.title}</div>
              <div className="text-base text-black text-left">{item.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const FaqSection = ({ faq, title }) => {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <section className="w-full max-w-2xl mx-auto px-4 py-12">
      <div className="flex-1 flex flex-col justify-center mb-10 lg:mb-0">
        <h2 className="text-[2.5rem] lg:text-[3.4rem] font-extrabold leading-tight">
          {title}
        </h2>
      </div>
      {faq.map((item, idx) => (
        <div key={idx}>
          <button
            onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
            className="w-full flex items-center justify-between py-6 focus:outline-none"
          >
            <span className="text-left text-[1.6rem] font-bold leading-tight">
              {item.q}
            </span>
            <span
              className="text-4xl font-light text-black select-none transition-transform"
              style={{
                transform: openIdx === idx ? "rotate(45deg)" : "rotate(0)",
              }}
            >
              +
            </span>
          </button>
          <div className="border-t border-black" />
          {openIdx === idx && (
            <div className="px-2 py-4 text-lg text-black">{item.a}</div>
          )}
        </div>
      ))}
    </section>
  );
};

const InterestedSection = ({ t }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language === "id" ? "id" : "en";

  return (
    <section className="w-full bg-gradient-to-tr from-[#e0ecff] to-[#f8fafc] py-10 px-4 sm:px-0 mb-8">
      <div className="max-w-2xl mx-auto flex flex-col items-center gap-4 text-center">
        <div className="text-2xl sm:text-3xl font-bold text-black mb-2">
          {t("common.ctaTitle", { ns: "services" })}
        </div>
        <div className="text-base sm:text-lg text-gray-700 mb-6">
          {t("common.ctaBody", { ns: "services" })}
        </div>
        <Link
          to={`/${lang}/get-started`}
          className="inline-flex items-center px-6 py-3 rounded-full bg-black text-white font-semibold text-lg shadow-md hover:bg-gray-800 focus:bg-gray-800 transition"
        >
          {t("common.ctaBtn", { ns: "services" })}
        </Link>
      </div>
    </section>
  );
};

const ServiceDetailPage = () => {
  const { serviceName } = useParams();
  const key = serviceName?.toLowerCase();
  const { t } = useTranslation("services");

  // Scroll to top when page opens
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (key !== "buy" && key !== "rent") {
    return <Redirect to="/404" replace />;
  }

  // read all text from i18n
  const features = t(`${key}.features`, { returnObjects: true });
  const firstExplanation = t(`${key}.firstExplanation`, {
    returnObjects: true,
  });


  const secondExplanation = t(`${key}.secondExplanation`, {
    returnObjects: true,
  });
  const benefits = t(`${key}.benefits`, { returnObjects: true });
  const faq = t(`${key}.faq`, { returnObjects: true });
  const main = t(`${key}.main`);
  const sub = t(`${key}.sub`);

  return (
    <div>
      {/* If you want hero back later, read hero title/subtitle via t(`${key}.hero.*`) */}
      <AnimatedSection>
        <FeatureSection
          features={features}
          headline={main || t("common.featuresHeadline")}
          subtitle={sub || t("common.featuresSubtitle")}
          serviceKey={key}
        />
      </AnimatedSection>

      <AnimatedSection>
        <FirstExplanationSection
          data={firstExplanation}
          image={IMAGES[key].first}
        />
      </AnimatedSection>

      {/* <AnimatedSection>
        <SecondExplanationSection
          data={secondExplanation}
          image={IMAGES[key].second}
        />
      </AnimatedSection> */}

      <InterestedSection t={t} />
      {/* 
      <AnimatedSection>
        <BenefitsSection benefits={benefits} title={t("common.keyBenefits")} />
      </AnimatedSection> */}

      <AnimatedSection>
        <FaqSection faq={faq} title={t("common.faq")} />
      </AnimatedSection>
    </div>
  );
};

export default ServiceDetailPage;
